// src/pages/Consultation/tabs/DetailsTab.jsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  FiUser,
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiCalendar,
  FiEdit3,
  FiSave,
  FiX,
  FiPause,
  FiPlay,
  FiInfo,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";

const DetailsTab = ({ session, onUpdateNotes }) => {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState({
    sessionNotes: "",
    customerBehavior: "",
    closingNotes: "",
    internalNotes: "",
  });
  const [savingNotes, setSavingNotes] = useState(false);

  // Initialize notes from session
  useEffect(() => {
    if (session?.adminNotes) {
      setNotes({
        sessionNotes: session.adminNotes.sessionNotes || "",
        customerBehavior: session.adminNotes.customerBehavior || "",
        closingNotes: session.adminNotes.closingNotes || "",
        internalNotes: session.adminNotes.internalNotes || "",
      });
    }
  }, [session]);

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return "0 min";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  // Calculate total break time
  const calculateTotalBreakTime = () => {
    if (!session?.breaks || session.breaks.length === 0) return 0;
    return session.breaks.reduce((total, br) => total + (br.duration || 0), 0);
  };

  // Handle notes change
  const handleNotesChange = (field, value) => {
    setNotes((prev) => ({ ...prev, [field]: value }));
  };

  // Save notes
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await onUpdateNotes(notes);
      toast.success("Notes saved successfully");
      setIsEditingNotes(false);
    } catch (error) {
      console.error("Save notes error:", error);
      toast.error(error?.message || "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    // Reset notes to session data
    if (session?.adminNotes) {
      setNotes({
        sessionNotes: session.adminNotes.sessionNotes || "",
        customerBehavior: session.adminNotes.customerBehavior || "",
        closingNotes: session.adminNotes.closingNotes || "",
        internalNotes: session.adminNotes.internalNotes || "",
      });
    }
    setIsEditingNotes(false);
  };

  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No session data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Session Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiInfo className="text-primary" />
            Session Information
          </h3>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              session.status === "ACTIVE"
                ? "bg-green-100 text-green-800"
                : session.status === "PAUSED"
                ? "bg-yellow-100 text-yellow-800"
                : session.status === "COMPLETED"
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {session.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <InfoRow
              icon={FiCalendar}
              label="Session Code"
              value={session.sessionCode}
            />
            <InfoRow
              icon={FiCalendar}
              label="Session Type"
              value={session.sessionType}
            />
            <InfoRow
              icon={FiCalendar}
              label="Start Time"
              value={formatDate(session.startTime)}
            />
            {session.actualEndTime && (
              <InfoRow
                icon={FiCalendar}
                label="End Time"
                value={formatDate(session.actualEndTime)}
              />
            )}
            <InfoRow
              icon={FiClock}
              label="Duration"
              value={formatDuration(session.duration)}
            />
            <InfoRow
              icon={FiClock}
              label="Total Break Time"
              value={formatDuration(calculateTotalBreakTime())}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <InfoRow
              icon={FiMapPin}
              label="Branch"
              value={session.branchCode}
            />
            {session.storeCode && (
              <InfoRow
                icon={FiMapPin}
                label="Store"
                value={session.storeCode}
              />
            )}
            <InfoRow
              icon={FiUser}
              label="Primary Admin"
              value={`${session.primaryAdmin.adminName} (${session.primaryAdmin.adminCode})`}
            />
            {session.metadata?.source && (
              <InfoRow
                icon={FiInfo}
                label="Source"
                value={session.metadata.source}
              />
            )}
            {session.metadata?.channel && (
              <InfoRow
                icon={FiInfo}
                label="Channel"
                value={session.metadata.channel}
              />
            )}
            {session.appointmentId && (
              <InfoRow
                icon={FiCalendar}
                label="Appointment ID"
                value={session.appointmentId}
              />
            )}
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiUser className="text-primary" />
          Customer Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {session.userId ? (
            <>
              <InfoRow
                icon={FiUser}
                label="Customer ID"
                value={session.customerId}
              />
              <InfoRow icon={FiUser} label="User ID" value={session.userId} />
              {/* Additional customer data would come from user profile */}
            </>
          ) : session.guestCustomer ? (
            <>
              <InfoRow
                icon={FiUser}
                label="Guest Name"
                value={session.guestCustomer.name || "N/A"}
              />
              <InfoRow
                icon={FiPhone}
                label="Phone"
                value={session.guestCustomer.phoneNumber || "N/A"}
              />
              <InfoRow
                icon={FiMail}
                label="Email"
                value={session.guestCustomer.email || "N/A"}
              />
              {session.guestCustomer.capturedAt && (
                <InfoRow
                  icon={FiCalendar}
                  label="Details Captured"
                  value={formatDate(session.guestCustomer.capturedAt)}
                />
              )}
            </>
          ) : (
            <p className="text-gray-500 col-span-2">
              Customer information not available
            </p>
          )}
        </div>
      </div>

      {/* Customer Engagement Stats */}
      {session.customerEngagement && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Customer Engagement
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Products Shown"
              value={session.customerEngagement.productsShown?.length || 0}
              color="blue"
            />
            <StatCard
              label="Products Tried"
              value={session.customerEngagement.productsTried?.length || 0}
              color="purple"
            />
            <StatCard
              label="Cart Views"
              value={session.customerEngagement.cartViewedCount || 0}
              color="green"
            />
            <StatCard
              label="App Connected"
              value={
                session.customerEngagement.customerAppConnected ? "Yes" : "No"
              }
              color={
                session.customerEngagement.customerAppConnected
                  ? "green"
                  : "gray"
              }
            />
          </div>
        </div>
      )}

      {/* Breaks/Pauses History */}
      {session.breaks && session.breaks.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiPause className="text-warning" />
            Breaks & Pauses History
          </h3>

          <div className="space-y-3">
            {session.breaks.map((br, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-warning-light rounded-lg">
                    <FiPause className="text-warning-dark" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {br.reason || "Break"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(br.pausedAt)} →{" "}
                      {br.resumedAt ? formatDate(br.resumedAt) : "In progress"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {formatDuration(br.duration)}
                  </p>
                  {br.resumedAt && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <FiPlay size={12} />
                      Resumed
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Total Break Time:</strong>{" "}
              {formatDuration(calculateTotalBreakTime())}
            </p>
          </div>
        </div>
      )}

      {/* Admin Notes */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiEdit3 className="text-primary" />
            Admin Notes
          </h3>
          {!isEditingNotes ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditingNotes(true)}
              disabled={session.status === "COMPLETED"}
            >
              <FiEdit3 className="mr-2" />
              Edit Notes
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={savingNotes}
              >
                <FiX className="mr-2" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveNotes}
                loading={savingNotes}
                disabled={savingNotes}
              >
                <FiSave className="mr-2" />
                Save
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Session Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Notes
            </label>
            {isEditingNotes ? (
              <textarea
                value={notes.sessionNotes}
                onChange={(e) =>
                  handleNotesChange("sessionNotes", e.target.value)
                }
                disabled={savingNotes}
                rows={4}
                placeholder="General notes about the session..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[100px]">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {notes.sessionNotes || "No notes added"}
                </p>
              </div>
            )}
          </div>

          {/* Customer Behavior */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Behavior
            </label>
            {isEditingNotes ? (
              <textarea
                value={notes.customerBehavior}
                onChange={(e) =>
                  handleNotesChange("customerBehavior", e.target.value)
                }
                disabled={savingNotes}
                rows={3}
                placeholder="Observations about customer preferences, behavior..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[80px]">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {notes.customerBehavior || "No notes added"}
                </p>
              </div>
            )}
          </div>

          {/* Closing Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Closing Notes
            </label>
            {isEditingNotes ? (
              <textarea
                value={notes.closingNotes}
                onChange={(e) =>
                  handleNotesChange("closingNotes", e.target.value)
                }
                disabled={savingNotes}
                rows={3}
                placeholder="Final observations, next steps..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[80px]">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {notes.closingNotes || "No notes added"}
                </p>
              </div>
            )}
          </div>

          {/* Internal Notes */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              Internal Notes
              <span className="text-xs text-gray-500 font-normal">
                (Not visible to customer)
              </span>
            </label>
            {isEditingNotes ? (
              <textarea
                value={notes.internalNotes}
                onChange={(e) =>
                  handleNotesChange("internalNotes", e.target.value)
                }
                disabled={savingNotes}
                rows={3}
                placeholder="Private notes for internal use only..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            ) : (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200 min-h-[80px]">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {notes.internalNotes || "No internal notes added"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Outcome (if completed) */}
      {session.outcome && session.status === "COMPLETED" && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Session Outcome
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Converted</span>
              <span
                className={`font-medium ${
                  session.outcome.converted ? "text-green-600" : "text-red-600"
                }`}
              >
                {session.outcome.converted ? "Yes" : "No"}
              </span>
            </div>

            {session.outcome.orderId && (
              <InfoRow
                icon={FiInfo}
                label="Order ID"
                value={session.outcome.orderId}
              />
            )}

            {session.outcome.noPurchaseReason && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  No Purchase Reason
                </p>
                <p className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-700">
                  {session.outcome.noPurchaseReason}
                </p>
              </div>
            )}

            {session.outcome.customerRating && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Customer Rating:
                </span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={
                        i < session.outcome.customerRating
                          ? "text-yellow-400"
                          : "text-gray-300"
                      }
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            )}

            {session.outcome.customerFeedback && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Customer Feedback
                </p>
                <p className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-700">
                  {session.outcome.customerFeedback}
                </p>
              </div>
            )}

            {session.outcome.followUpRequired && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-blue-900 mb-2">
                  Follow-up Required
                </p>
                {session.outcome.followUpDate && (
                  <p className="text-sm text-blue-700">
                    Date: {formatDate(session.outcome.followUpDate)}
                  </p>
                )}
                {session.outcome.followUpNotes && (
                  <p className="text-sm text-blue-700 mt-2">
                    {session.outcome.followUpNotes}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="text-primary mt-0.5 flex-shrink-0" />
    <div className="flex-1">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div className={`p-4 bg-${color}-50 rounded-lg border border-${color}-200`}>
    <p className={`text-2xl font-bold text-${color}-700 mb-1`}>{value}</p>
    <p className={`text-sm text-${color}-600`}>{label}</p>
  </div>
);

export default DetailsTab;
